import { css, Global } from '@emotion/react';
import { useState } from 'react';
import { type GooglePlacesSelectedPlace, useGooglePlacesAutocomplete } from '../../State';
import { LAYOUT2 } from '../../Styles';
import { LabeledInputSize, LabeledInputStyle } from './LabeledInputCommonTypes';
import { LabeledInputPresenter } from './LabeledInputPresenter';
import { LABELED_INPUT_SLOT_SIZE_STYLES } from './labeledInputSlotSizesLib';

export type GooglePlacesAutocompleteInputProps = {
  googlePlacesApiKey: string;
  onPlaceSelected?: (place: GooglePlacesSelectedPlace) => void | Promise<void>;
  options?: {
    types?: string[];
    componentRestrictions?: { country: string | string[] };
    fields?: string[];
  };
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  style?: LabeledInputStyle;
  size?: LabeledInputSize;
};

/**
 * Tighter horizontal inset for the Places widget than `INPUT_CONTAINER_SIZE_STYLES` (e.g. medium’s
 * `spacing2`). Targets the presenter’s bordered frame via `data-input-container`.
 */
const GOOGLE_PLACES_INPUT_FRAME_PADDING_OVERRIDE = css`
  label: GOOGLE_PLACES_INPUT_FRAME_PADDING_OVERRIDE;
  & [data-input-container] {
    padding-left: ${LAYOUT2.spacingHalf}px;
    padding-right: ${LAYOUT2.spacingHalf}px;
  }
`;

/**
 * `PlaceAutocompleteElement` draws its own control. The outer border must come only from
 * `LabeledInputPresenter` — a second border here looked like a second “input”.
 * Stretch the web component to fill the presenter’s inner flex row (same footprint as `<input>`).
 */
const GOOGLE_PLACES_WIDGET_SLOT_STYLE = css`
  label: GOOGLE_PLACES_WIDGET_SLOT_STYLE;
  flex: 1;
  align-self: stretch;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  padding: 0;
  margin: 0;
`;

const GOOGLE_PLACE_AUTOCOMPLETE_STYLES = css`
  /* Host: no second border — LabeledInputPresenter supplies the frame. */
  gmp-place-autocomplete,
  gmp-place-autocomplete:hover,
  gmp-place-autocomplete:focus,
  gmp-place-autocomplete:focus-visible,
  gmp-place-autocomplete:focus-within {
    flex: 1;
    width: 100%;
    min-width: 0;
    height: 100%;
    min-height: 0;
    display: block;
    box-sizing: border-box;
    color-scheme: light;
    --font-family: "CircularXX", -apple-system, BlinkMacSystemFont, sans-serif;
    border: none !important;
    border-radius: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
    color: var(--text);
    /* CSS custom properties that propagate into shadow DOM — cover both
       Google Maps UI Kit tokens and Material Web component tokens. */
    --gmpx-color-primary: transparent;
    --gmpx-color-outline: transparent;
    --md-sys-color-primary: transparent;
    --md-focus-ring-color: transparent;
    --md-focus-ring-width: 0px;
    --md-outlined-text-field-focus-outline-color: transparent;
    --md-outlined-text-field-focus-outline-width: 0px;
    --md-filled-text-field-active-indicator-color: transparent;
    --md-filled-text-field-focus-active-indicator-color: transparent;
  }

  gmp-place-autocomplete::part(input),
  gmp-place-autocomplete::part(input):focus,
  gmp-place-autocomplete::part(input):focus-visible {
    font-family: "CircularXX", -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--text);
    background: transparent;
    border: none !important;
    border-radius: 0 !important;
    outline: none !important;
    box-shadow: none !important;
  }

  /* Google Places UI Kit focus ring (when selector matches). */
  gmp-place-autocomplete .focus-ring {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }
`;

/** US-only address search; `options.componentRestrictions.country` cannot override this. */
export const GooglePlacesAutocompleteInput = ({
  googlePlacesApiKey,
  onPlaceSelected,
  options,
  label,
  value,
  onChange,
  placeholder,
  style = 'normal',
  size = 'medium',
}: GooglePlacesAutocompleteInputProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const { containerRef } = useGooglePlacesAutocomplete({
    apiKey: googlePlacesApiKey,
    controlledValue: value,
    onInputChange: onChange,
    onPlaceSelected,
    placeholder,
    options: {
      ...options,
      componentRestrictions: { country: 'US' },
    },
  });

  return (
    <>
      <Global styles={GOOGLE_PLACE_AUTOCOMPLETE_STYLES} />
      <div css={GOOGLE_PLACES_INPUT_FRAME_PADDING_OVERRIDE}>
        <LabeledInputPresenter
          width={undefined}
          style={style}
          size={size}
          onClick={() => {}}
          title=''
          isFocused={isFocused}
          label={label}
          optional={false}
          unitBefore={false}
          disabled={false}
        >
          <div
            ref={containerRef}
            css={[
              GOOGLE_PLACES_WIDGET_SLOT_STYLE,
              LABELED_INPUT_SLOT_SIZE_STYLES[size],
            ]}
            onFocusCapture={() => setIsFocused(true)}
            onBlurCapture={() => setIsFocused(false)}
            data-size={size}
          />
        </LabeledInputPresenter>
      </div>
    </>
  );
};
